import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Dialog, Field, Input, Select, useApi, useInvalidateResource, useToast } from '../../engine';
import { ApiError } from '../../engine/api/client';
import { useMeta } from '../../engine/meta/useMeta';

// Team primary action: invite a teammate. Creating an invite is the `invites` resource's
// create (POST /v1/invites, owner/admin) through the typed client; the response carries the
// token once so the accept link can be copied without waiting for the email. The role list is
// the resource meta's enum (every membership role), never a second copy. `?new=1` (palette /
// "Invite" command) opens it.

interface InviteResult {
  email: string;
  token: string;
  invite_url?: string;
}

const FALLBACK_ROLES = [{ value: 'member', label: 'Member' }, { value: 'admin', label: 'Admin' }];

/** Members list "Member" column: joined DTO carries full_name + user_email (not meta fields). */
export function MemberCell({ row }: { row: Record<string, unknown> }) {
  const name = (row.full_name as string | null) ?? null;
  const email = (row.user_email as string | null) ?? '';
  return (
    <span>
      <strong>{name ?? email}</strong>
      {name && email && <span className="hf-muted"> · {email}</span>}
    </span>
  );
}

export function InviteAction() {
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateResource();
  const meta = useMeta('invites');
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(() => params.has('new'));
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const roles = meta.data?.fields.find((f) => f.name === 'role')?.enum ?? FALLBACK_ROLES;

  const close = () => {
    setOpen(false);
    setError(null);
    if (params.has('new')) {
      const next = new URLSearchParams(params);
      next.delete('new');
      setParams(next, { replace: true });
    }
  };

  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const inv = await api.resource<InviteResult, { email: string; role: string }>('invites').create({ email: email.trim(), role });
      invalidate('invites');
      const url = new URL(inv.invite_url ?? `/invite?token=${inv.token}`, window.location.origin).href;
      toast({ title: `Invite sent to ${inv.email}`, detail: 'They will get a branded email with a one-click accept link.', action: { label: 'Copy link', run: () => void navigator.clipboard?.writeText(url) } });
      setEmail('');
      setRole('member');
      close();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="primary" icon="user-plus" onClick={() => setOpen(true)}>Invite someone</Button>
      <Dialog
        open={open}
        onClose={close}
        title="Invite a teammate"
        description="They will receive a branded email with a one-click accept link. Invites expire after 14 days and only work for the invited address. Re-inviting an address replaces its pending invite."
        footer={
          <>
            <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
            <Button variant="primary" busy={busy} disabled={!email.trim()} onClick={() => void submit()}>Send invite</Button>
          </>
        }
      >
        <form
          className="hf-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label="Email" id="inv-email" error={error ?? undefined}>
            <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" autoFocus required />
          </Field>
          <Field label="Role" id="inv-role" help="Owner can only be granted by an owner.">
            <Select id="inv-role" value={role} onChange={(e) => setRole(e.target.value)} options={roles.map((r) => ({ value: r.value, label: r.label }))} />
          </Field>
        </form>
      </Dialog>
    </>
  );
}
